/**
 * ENG-4644: Dexie IndexedDB schema for the POS local-first system.
 *
 * Tables:
 *   mutations       – Sync queue: mutations survive page reloads and are
 *                     replayed on app boot / batched on flushNow.
 *   menu_cache      – Full menu groups per store (replaces localStorage).
 *   items_cache     – Menu items per group (replaces in-memory Map L2).
 *   modifiers_cache – Modifiers per item (replaces in-memory Map L2).
 *   store_config    – Full store config (replaces BehaviorSubject-only).
 *   order_snapshots – Local order state snapshots for reload resilience.
 */
import Dexie, { Table } from 'dexie';

// ─── Mutation types ──────────────────────────────────────────────

export type MutationType =
  | 'add_item'
  | 'update_quantity'
  | 'update_modifiers'
  | 'void_item'
  | 'apply_discount'
  | 'remove_discount'
  | 'update_tip'
  | 'add_misc_item'
  | 'delete_misc_item'
  | 'update_occasion'
  | 'update_customer_info'
  | 'update_instruction'
  | 'update_tax_exempt'
  | 'apply_promo'
  | 'remove_promo'
  // Seat numbers: move lines between seats / set how many guests sit at the table.
  | 'set_seat'
  | 'update_guest_count';

export type MutationStatus = 'pending' | 'syncing' | 'committed' | 'rejected';

export interface StoredMutation {
  /** Auto-incremented PK in IndexedDB */
  id?: number;
  /** UUID generated on the client — dedup key on the server */
  localId: string;
  /** Order hash this mutation belongs to */
  orderHash: string;
  /** Mutation type */
  type: MutationType;
  /** Type-specific payload */
  payload: any;
  /** Temp item ID for add_item mutations */
  tempItemId?: string;
  /** Date.now() when the user performed the action */
  timestamp: number;
  /** Current sync status */
  status: MutationStatus;
}

// ─── Cache table types ───────────────────────────────────────────

export interface CachedMenuGroups {
  /** Store ID — primary key */
  storeId: string;
  /** Full menu groups array (MenuItemGroup[]) */
  data: any;
  /** Date.now() when cached */
  ts: number;
  /** True when this record was written by a full syncFullMenu() HTTP response */
  complete?: boolean;
}

export interface CachedMenuItems {
  /** Store ID (part of compound PK) */
  storeId: string;
  /** Menu group ID (part of compound PK) */
  groupId: string;
  /** Menu items array */
  data: any;
  /** Date.now() when cached */
  ts: number;
}

export interface CachedModifiers {
  /** Store ID (part of compound PK) */
  storeId: string;
  /** Menu item ID (part of compound PK) */
  itemId: string;
  /** Modifier groups array */
  data: any;
  /** Date.now() when cached */
  ts: number;
}

export interface CachedStoreConfig {
  /** Store ID — primary key */
  storeId: string;
  /** Full Store object */
  data: any;
  /** Date.now() when cached */
  ts: number;
}

export interface CachedOrderSnapshot {
  /** Order hash — primary key */
  orderHash: string;
  /** Full LocalOrderState */
  state: any;
  /** Date.now() when snapshot taken */
  ts: number;
}

// ─── Database class ──────────────────────────────────────────────

export class PosSyncDB extends Dexie {
  mutations!: Table<StoredMutation, number>;
  menu_cache!: Table<CachedMenuGroups, string>;
  items_cache!: Table<CachedMenuItems, [string, string]>;
  modifiers_cache!: Table<CachedModifiers, [string, string]>;
  store_config!: Table<CachedStoreConfig, string>;
  order_snapshots!: Table<CachedOrderSnapshot, string>;

  constructor() {
    super('pos_sync_queue');

    // v1: mutations only
    this.version(1).stores({
      mutations: '++id, orderHash, status, localId',
    });

    // v2: add cache tables for menu, store, and order persistence
    this.version(2).stores({
      mutations: '++id, orderHash, status, localId',
      menu_cache: 'storeId',
      items_cache: '[storeId+groupId], storeId',
      modifiers_cache: '[storeId+itemId], storeId',
      store_config: 'storeId',
      order_snapshots: 'orderHash',
    });
  }
}

export const posSyncDb = new PosSyncDB();
